import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const TenantContext = createContext(null);

const DEFAULT_BRANDING = {
  tenant_id: null,
  app_name: 'Agentic FuturePath AI',
  logo_url: null,
  primary_color: '#3b82f6',
  secondary_color: '#1e3a5f',
  accent_color: '#06b6d4',
  dark_bg: '#0a1628',
  tagline: 'AI-Powered Career Navigation',
  is_white_label: false,
};

const API = process.env.REACT_APP_BACKEND_URL;

export function TenantProvider({ children }) {
  const [branding, setBranding] = useState(() => {
    const saved = localStorage.getItem('tenant_branding');
    return saved ? JSON.parse(saved) : DEFAULT_BRANDING;
  });
  const [tenantSlug, setTenantSlug] = useState(() => localStorage.getItem('tenant_slug') || null);
  const [loading, setLoading] = useState(false);

  const applyBranding = useCallback((b) => {
    document.documentElement.style.setProperty('--tenant-primary', b.primary_color);
    document.documentElement.style.setProperty('--tenant-secondary', b.secondary_color);
    document.documentElement.style.setProperty('--tenant-accent', b.accent_color);
    document.documentElement.style.setProperty('--tenant-dark-bg', b.dark_bg);
    if (b.app_name && b.is_white_label) {
      document.title = b.app_name;
    }
  }, []);

  const loadTenant = useCallback(async (slug) => {
    if (!slug) {
      setBranding(DEFAULT_BRANDING);
      setTenantSlug(null);
      localStorage.removeItem('tenant_branding');
      localStorage.removeItem('tenant_slug');
      applyBranding(DEFAULT_BRANDING);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/tenant/branding/${slug}`);
      if (!res.ok) throw new Error('Tenant not found');
      const data = await res.json();
      setBranding(data);
      setTenantSlug(slug);
      localStorage.setItem('tenant_branding', JSON.stringify(data));
      localStorage.setItem('tenant_slug', slug);
      applyBranding(data);
    } catch (e) {
      console.error('Failed to load tenant:', e);
      setBranding(DEFAULT_BRANDING);
      applyBranding(DEFAULT_BRANDING);
    } finally {
      setLoading(false);
    }
  }, [applyBranding]);

  const clearTenant = useCallback(() => {
    setBranding(DEFAULT_BRANDING);
    setTenantSlug(null);
    localStorage.removeItem('tenant_branding');
    localStorage.removeItem('tenant_slug');
    applyBranding(DEFAULT_BRANDING);
  }, [applyBranding]);

  // Apply branding on mount
  useEffect(() => {
    applyBranding(branding);
  }, []);

  return (
    <TenantContext.Provider value={{ branding, tenantSlug, loading, loadTenant, clearTenant }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error('useTenant must be used within TenantProvider');
  return ctx;
}
